// src/pages/ManageProgramPage.js
import React, { useState, useRef } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useProgram } from '../context/ProgramContext';
import apiClient from '../api';
import { QRCodeCanvas } from 'qrcode.react';
import '../styles/ManageProgramPage.css';
import defaultProgramIcon from '../assets/icons/default-program-icon.svg';
import fakeQrCode from '../assets/icons/fake-qr-code.png';

const ManageProgramPage = ({ isAuthenticated }) => {
    const { selectedProgram, setSelectedProgram } = useProgram();
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const qrCodeWrapperRef = useRef(null);

    const [asaInput, setAsaInput] = useState('');
    const [asaSubmitting, setAsaSubmitting] = useState(false);
    const [asaError, setAsaError] = useState('');
    const [asaSuccess, setAsaSuccess] = useState('');

    const handleAttachAsa = async (e) => {
        e.preventDefault();
        setAsaError(''); setAsaSuccess('');
        const parsed = parseInt(asaInput.trim(), 10);
        if (!parsed || parsed <= 0) {
            setAsaError('Enter a valid ASA ID (positive integer).');
            return;
        }
        const confirmMsg = selectedProgram.asset_type === 'points_on_chain'
            ? 'Replace the current ASA with this one? Existing balances stay on the previous ASA.'
            : 'Switch this program to on-chain mode with this ASA? Off-chain points will no longer be displayed for new participants.';
        if (!window.confirm(confirmMsg)) return;
        setAsaSubmitting(true);
        try {
            const res = await apiClient.post(`/programs/${selectedProgram.id}/asa`, { asa_id: parsed });
            setSelectedProgram(res.data);
            setAsaSuccess(`Attached ASA ${res.data.asa_id} (${res.data.asa_unit_name || 'no unit name'}, ${res.data.asa_decimals} decimals).`);
            setAsaInput('');
        } catch (err) {
            setAsaError(err.response?.data?.detail || err.message || 'Failed to attach ASA.');
        } finally {
            setAsaSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    if (!selectedProgram) {
        return (
            <div className="manage-program-container info-message">
                <h2>No Program Selected</h2>
                <p>Please <Link to="/select-program">select a program</Link> first to manage it.</p>
            </div>
        );
    }

    // In dev (different ports for admin/user) fall back to localhost:3001 so the link
    // doesn't loop back to the admin app. In prod the same-origin fallback works because
    // nginx routes `/` to the user app and `/admin/` to the admin app.
    const inferUserAppUrl = () => {
        if (process.env.REACT_APP_USER_APP_URL) return process.env.REACT_APP_USER_APP_URL;
        const { protocol, hostname, port } = window.location;
        if (port === '3000' && (hostname === 'localhost' || hostname === '127.0.0.1')) {
            return `${protocol}//${hostname}:3001`;
        }
        return window.location.origin;
    };
    const userAppBaseUrl = inferUserAppUrl();
    const programUrl = `${userAppBaseUrl}/?p=${selectedProgram.short_name}`;

    const handleDownloadQrCode = () => {
        if (qrCodeWrapperRef.current) {
            const canvas = qrCodeWrapperRef.current.querySelector('canvas');
            if (canvas) {
                const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
                let downloadLink = document.createElement("a");
                downloadLink.href = pngUrl;
                downloadLink.download = `${selectedProgram.short_name}-url-qr.png`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            } else {
                console.error("QR Code Canvas not found for download.");
                alert("Could not download QR code. Ensure it is rendered as canvas.");
            }
        }
    };

    const handleDeleteProgram = async () => {
        if (!selectedProgram) return;
        const programName = selectedProgram.name;
        const programId = selectedProgram.id;
        const confirmed = window.confirm(
            `Are you absolutely sure you want to delete the program "${programName}"?\n\n` +
            `This action is permanent and cannot be undone. All associated participants, points, and data for this program will be lost.`
        );
        if (!confirmed) return;
        setIsDeleting(true); setDeleteError('');
        try {
            await apiClient.delete(`/programs/${programId}`);
            setSelectedProgram(null); navigate('/select-program');
        } catch (err) {
            setDeleteError(err.response?.data?.detail || err.message || 'Failed to delete.');
            setIsDeleting(false);
        }
    };

    let programIconSrc = defaultProgramIcon;
    if (selectedProgram.logo_url) {
        if (selectedProgram.logo_url.startsWith('http')) {
            programIconSrc = selectedProgram.logo_url;
        } else {
            programIconSrc = `${apiClient.defaults.baseURL}${selectedProgram.logo_url}`;
        }
    }

    return (
        <div className="manage-program-container">
            <div className="manage-header">
                 <h2>Manage Program: {selectedProgram.name}</h2>
            </div>
             {deleteError && (<p className="error-message form-error-message">Error: {deleteError}</p>)}
            <p>Configure details and design the user card for your program: <strong>{selectedProgram.short_name}</strong>.</p>

            <div className="selected-program-details">
                <img src={programIconSrc} alt={`${selectedProgram.name} Logo`} className="details-program-icon" />
                <div>
                    <p><strong>Name:</strong> {selectedProgram.name}</p>
                    <p><strong>Short Name:</strong> <code>{selectedProgram.short_name}</code></p>
                    <p><strong>Asset Type:</strong> {selectedProgram.asset_type}</p>
                    {selectedProgram.asset_type === 'points_on_chain' && selectedProgram.asa_id && (
                        <p><strong>ASA:</strong> {selectedProgram.asa_unit_name || '—'} (ID {selectedProgram.asa_id}, {selectedProgram.asa_decimals} decimals)</p>
                    )}
                </div>
            </div>

            <div className="asa-config-section">
                <h2>On-chain (ASA) Configuration</h2>
                <p>
                    Paste an existing Algorand Standard Asset (ASA) ID to make this program distribute that
                    token. Decimals and unit name are read from the chain. Your wallet must already hold the
                    asset (or be its creator) to award points later.
                </p>
                <form onSubmit={handleAttachAsa} className="asa-attach-form">
                    <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="e.g. 393498731"
                        value={asaInput}
                        onChange={(e) => setAsaInput(e.target.value)}
                        disabled={asaSubmitting}
                    />
                    <button type="submit" disabled={asaSubmitting || !asaInput.trim()}>
                        {asaSubmitting ? 'Attaching…' : (selectedProgram.asa_id ? 'Replace ASA' : 'Attach ASA')}
                    </button>
                </form>
                {asaError && <p className="error-message form-error-message">{asaError}</p>}
                {asaSuccess && <p className="success-message">{asaSuccess}</p>}
            </div>

            <div className="program-access-section">
                <h2>Program Access Link</h2>
                <p>Share this link with your users to access the program:</p>
                <div className="program-url-display">
                    <a href={programUrl} target="_blank" rel="noopener noreferrer">{programUrl}</a>
                </div>
                <div className="program-qr-code-display" ref={qrCodeWrapperRef}>
                    <QRCodeCanvas
                        value={programUrl}
                        size={180}
                        level={"H"}
                        includeMargin={true}
                    />
                </div>
                <button onClick={handleDownloadQrCode} className="download-qr-button">
                    Download QR Code
                </button>
            </div>

            <div className="card-design-area">
                <h2>User Card Preview</h2>
                <p>This is a preview of how the user's card will look in Apple Wallet.</p>
                <div className="wallet-card-container">
                    <div className="wallet-card">
                        <div className="wallet-card-header">
                            <img src={programIconSrc} alt="Program Icon" className="wallet-card-icon" />
                            <span className="wallet-card-program-name">{selectedProgram.name}</span>
                        </div>
                        <div className="wallet-card-body">
                            <div className="wallet-card-points">
                                <span className="points-value">1,234</span>
                                <span className="points-label">Current Points</span>
                            </div>
                            <div className="wallet-card-member">
                                <span className="field-label">MEMBER</span>
                                <span className="field-value">Jane Appleseed</span>
                            </div>
                            <div className="wallet-card-qr">
                                <img src={fakeQrCode} alt="Participant QR" className="qr-code-img" />
                                <span className="qr-code-identifier">N4XV3R743YVSSBPFBW4CVDN326SN…</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="delete-button-container">
                <button onClick={handleDeleteProgram} className="delete-program-button" disabled={isDeleting}>
                     {isDeleting ? 'Deleting...' : 'Delete Program'}
                 </button>
            </div>
        </div>
    );
};

export default ManageProgramPage;
